import React, { useState, useEffect } from 'react';

// Nostalgic "photo album" gallery — each image is a taped polaroid with a
// warm vintage tint, a slight random tilt, and a straighten-on-hover lift.
const ImageGallery = () => {
  // Loaded from public/camera/manifest.json (generated by
  // scripts/gen-gallery-manifest.js). Lets us show every image in the
  // camera folder without hardcoding filenames.
  const [images, setImages] = useState([]);

  useEffect(() => {
    let cancelled = false;
    fetch('/camera/manifest.json')
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => {
        if (!cancelled && Array.isArray(list)) setImages(list);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // multiple of the desktop column count (4) so rows are always full — no gaps
  const initialImageCount = 12;
  const [visibleImages, setVisibleImages] = useState(initialImageCount);
  const [hasExpanded, setHasExpanded] = useState(false);

  const loadMoreImages = () => {
    setVisibleImages((prevCount) => Math.min(prevCount + 12, images.length));
    setHasExpanded(true);
  };

  const collapseGallery = () => {
    setVisibleImages(initialImageCount);
    setHasExpanded(false);
  };

  // deterministic pseudo-random tilt per index (stable across renders)
  const tiltFor = (i) => {
    const seq = [-3.5, 2.4, -1.6, 3.2, -2.8, 1.4, -3, 2, -1.2, 2.9];
    return seq[i % seq.length];
  };

  // Show full rows only (no empty slots) while there are more to load.
  // Once every image is shown, allow a partial final row (unavoidable).
  const showAll = visibleImages >= images.length;
  const shownCount = showAll ? images.length : Math.floor(visibleImages / 4) * 4;
  const shown = images.slice(0, shownCount);

  return (
    <div>
      {/* warm paper backdrop for the album feel */}
      <div className="relative rounded-3xl border border-[var(--z-line)] bg-[#fbf3e4] px-4 py-8 sm:px-8 shadow-inner">
        {/* faint vignette so the middle feels like an open album spread */}
        <div
          className="pointer-events-none absolute inset-0 rounded-3xl"
          style={{
            background:
              'radial-gradient(120% 90% at 50% 40%, transparent 55%, rgba(92,70,40,0.12) 100%)',
          }}
        />

        <div className="relative grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-8 sm:gap-y-10">
          {shown.map((src, index) => {
            const tilt = tiltFor(index);
            return (
              <figure
                key={index}
                className="polaroid group relative mx-auto w-full max-w-[220px]"
                style={{ '--tilt': `${tilt}deg` }}
              >
                {/* washi-tape corner */}
                <span className="absolute -top-2 left-1/2 -translate-x-1/2 z-10 h-4 w-14 rotate-[-4deg] rounded-[2px] bg-[var(--z-accent)]/40 shadow-sm" />

                {/* polaroid frame */}
                <div className="polaroid-frame rounded-[3px] bg-[#fffdf7] p-2 pb-4 shadow-[0_10px_22px_-10px_rgba(60,45,25,0.5)] ring-1 ring-black/5">
                  <div className="relative aspect-square overflow-hidden bg-[#e9e0cf]">
                    <img
                      src={src}
                      alt={`Gerak kerja Zabra ${index + 1}`}
                      loading="lazy"
                      className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                      style={{ filter: 'sepia(0.28) saturate(0.92) contrast(0.98) brightness(1.02)' }}
                    />
                    {/* aged photo tint + soft grain */}
                    <div
                      className="pointer-events-none absolute inset-0 mix-blend-multiply opacity-70 transition-opacity duration-500 group-hover:opacity-25"
                      style={{
                        background:
                          'linear-gradient(160deg, rgba(255,238,204,0.35), rgba(120,90,50,0.18))',
                      }}
                    />
                    <div className="pointer-events-none absolute inset-0 shadow-[inset_0_0_30px_rgba(70,50,25,0.28)]" />
                  </div>
                </div>
              </figure>
            );
          })}
        </div>
      </div>

      <div className="text-center mt-10 space-x-4">
        {shownCount < images.length && (
          <button onClick={loadMoreImages} className="btn-accent">
            Lihat Lagi
          </button>
        )}
        {hasExpanded && (
          <button onClick={collapseGallery} className="btn-outline">
            Simpan
          </button>
        )}
      </div>
    </div>
  );
};

export default ImageGallery;
